<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.documentList')"
        :size="60"
        @close="closeOverSide">
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.order_document_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleAddDocument">
                {{ $t('labels.addDocument') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="dataDocument"
            :headers="document"
            :server="server"
            :detail-row-auto-height="true"
            :use-archive="apiArchive"
            use-selection
            store-name="orderDataTable"
            store-key="OrderDocument" />
        <hts-modal
            :show="showPopupDeleteDocument"
            :title="$t('labels.deleteDocument')"
            type="confirm"
            :content="$t('labels.deleteDocumentContent')"
            @hide="closePopupDeleteDocument"
            @confirm="() => actionDeleteDocument()" />
        <edit-document
            ref="showDrawerEditDocument"
            :document-info="documentSelected"
            :type="props.type"
            @refreshDataTables="refreshTables()" />
        <add-document
            ref="addDocument"
            type="vehicle"
            @refreshDataTables="refreshTables()" />
    </hts-drawer>
</template>

<script lang="ts" setup>
import AddDocument from './add-document.vue'
import EditDocument from './edit-document.vue'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'
const { $formatters } = useNuxtApp()

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'screen-lg'
  },
  column: {
    type: Number,
    default: 3
  },
  type: {
    type: String,
    default: ''
  },
  reload: {
    type: Boolean,
    default: false
  }
})
const visible = ref(false)
const server = ref({} as any)
const orderId = ref()
const openDrawerDocument = (id: string) => {
  visible.value = true
  if (props.reload) {
    refreshTables()
  }
  orderId.value = id
  server.value = {
    method: 'POST',
    url: `/order/${id}/document/list`
  }
}
defineExpose({
  openDrawerDocument
})
const closeOverSide = () => {
  visible.value = false
}
const handleEditDocument = (document: any) => {
  documentSelected.value = document
  if (showDrawerEditDocument.value) {
    showDrawerEditDocument.value.openEditDocument(document)
  }
}
const handleDeleteDocument = (document: any) => {
  showPopupDeleteDocument.value = true
  documentIsSelectedForDelete.value = document
}

const handleAddDocument = () => {
  if (addDocument.value) {
    addDocument.value.openAddDocument(orderId.value)
  }
}
const document = [
  {
    field: 'type',
    headerName: $t('labels.documentType'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data.type === 'BOOKING') {
        return $t('labels.booking')
      }
      if (params.data.type === 'OTHER') {
        return $t('labels.other')
      }
    }
  },
  {
    field: 'fileName',
    headerName: $t('labels.documentFileName'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'path',
    headerName: $t('labels.link'),
    sortable: true,
    valueFormatter: (params: any) => {
      return $formatters.cdnUrl(params.data)
    },
    onCellClicked: (params: any) => {
      window.open($formatters.cdnUrl(params.data))
    }
  },
  {
    field: 'description',
    headerName: $t('labels.description'),
    sortable: true
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'orderDataTable',
      storeName: 'OrderDocument',
      items: [
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.order_document_edit),
          click: handleEditDocument
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.order_document_delete),
          click: handleDeleteDocument,
          isArchive: true
        }
      ]
    }
  }
]

const showPopupDeleteDocument: any = ref(false)
const showDrawerEditDocument: any = ref(false)
const addDocument = ref()
const documentIsSelectedForDelete = ref<{ [key: string]: any }>({})
const documentSelected = ref({})
const dataDocument = ref()

const refreshTables = () => {
  if (dataDocument.value) {
    dataDocument.value.refresh()
  }
}

const closePopupDeleteDocument = () => {
  showPopupDeleteDocument.value = false
  documentIsSelectedForDelete.value = {}
}

const actionDeleteDocument = () => {
  $api($urlClient.order.document.delete, { body: { ids: [documentIsSelectedForDelete.value._id] }, uriParams: { ID: orderId.value } }).then((data) => {
    const resData: any = data?.data.value

    if (resData.success) {
      documentIsSelectedForDelete.value = {}
      refreshTables()
      showPopupDeleteDocument.value = false
      process.client && $toast().success($t('labels.deleteSuccess'))
    }
  })
}

const apiArchive = computed(() => {
  return { api: $urlClient.order.document.archive, permissions: checkPermission(layoutPermissions.order_document_archive) }
})
</script>
